import { BookingMode, DeliveryMode, HousekeepingType, ReceptionType, RequestKind, RequestLineKind, RequestStatus } from '@/enums/request'
import { dayjs, intlLocale } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import type { TagItem } from '@/types/Common'
import type { IInvitationBooking, IRequest, IRequestLine, IRequestSettings } from '@/types/Request'

/*
 * GUEST REQUESTS in the console (docs/API.md §13): the words and colours of a status, when a
 * request is still open, which buttons its status allows, and the defaults of the settings and
 * of an invitation's booking.
 */

/** A request's status as a badge: pending stands out, the end states are quiet. */
export const statusTag = (status: string): TagItem => {
    const label = i18n.global.t(`request_status.${status}`, status)

    switch (status) {
        case RequestStatus.Pending:   return { label, color: 'warning', variant: 'solid', icon: 'i-lucide-bell-ring' }
        case RequestStatus.Accepted:  return { label, color: 'info' }
        case RequestStatus.OnTheWay:  return { label, color: 'primary', icon: 'i-lucide-truck' }
        case RequestStatus.Delivered: return { label, color: 'success', icon: 'i-lucide-check' }
        case RequestStatus.Done:      return { label, color: 'success', icon: 'i-lucide-check' }
        case RequestStatus.Declined:  return { label, color: 'error' }
        default:                      return { label, color: 'neutral' }
    }
}

/**
 * A request's badge in the list and the detail: its status, or — for an order the guest cancelled
 * after the kitchen accepted it (§13.1.1) — "Gost je preklical (prepozno)", which needs noticing.
 */
export const requestTag = (request: IRequest): TagItem => {
    if (request.late_cancel) {
        return { label: i18n.global.t('requests.late_cancel'), color: 'error', variant: 'solid', icon: 'i-lucide-ban' }
    }

    return statusTag(request.status)
}

/** "Naročilo v sobo" / "Rezervacija". */
export const kindLabel = (kind: string): string => i18n.global.t(`request_kind.${kind}`, kind)

export const kindIcon = (kind: string): string => {
    switch (kind) {
        case RequestKind.RoomService:  return 'i-lucide-utensils'
        case RequestKind.Housekeeping: return 'i-lucide-spray-can'
        case RequestKind.Reception:    return 'i-lucide-concierge-bell'
        default:                       return 'i-lucide-calendar-check'
    }
}

/** "Pozna odjava" / "Dodatna noč" / "Taksi" / "Miza v restavraciji" (§13.7). */
export const receptionTypeLabel = (type: string): string => i18n.global.t(`reception_type.${type}`, type)

/** "Manjka" / "Potrebujem še" / "Zamenjaj" / "Ne deluje" (§13.6). */
export const housekeepingTypeLabel = (type: string): string => i18n.global.t(`housekeeping_type.${type}`, type)

/** The type's badge: broken red, a fresh one for the used one blue, missing or more amber. */
export const housekeepingTypeColor = (type: string): 'error' | 'info' | 'warning' => {
    switch (type) {
        case HousekeepingType.Broken:  return 'error'
        case HousekeepingType.Replace: return 'info'
        default:                       return 'warning'
    }
}

/**
 * Still to be handled (§13.1, §13.6): pending, a room service accepted or on the way, a
 * housekeeping request accepted.
 */
export const isOpen = (request: IRequest): boolean => {
    if (request.status === RequestStatus.Pending) return true

    if (request.kind === RequestKind.Housekeeping) return request.status === RequestStatus.Accepted

    const moving = request.status === RequestStatus.Accepted || request.status === RequestStatus.OnTheWay

    return request.kind === RequestKind.RoomService && moving
}

/** Which of the house's actions the request's kind and status allow (the API's transitions). */
export const allowedActions = (request: IRequest) => {
    const roomService  = request.kind === RequestKind.RoomService
    const housekeeping = request.kind === RequestKind.Housekeeping
    const status       = request.status
    const handling     = status === RequestStatus.Pending || status === RequestStatus.Accepted

    return {
        accept:    status === RequestStatus.Pending,
        decline:   handling,
        onTheWay:  roomService && status === RequestStatus.Accepted,
        delivered: roomService && (status === RequestStatus.Accepted || status === RequestStatus.OnTheWay),
        // "Urejeno" (§13.6): straight from pending too — the item is often brought at once.
        done:      housekeeping && handling,
    }
}

/** A local date-time ("YYYY-MM-DDTHH:MM", the tenant's clock) as "24. 10. 19:30". */
export const formatLocal = (value: string): string => {
    if (!value) return ''

    const at = dayjs(value)

    if (!at.isValid()) return value

    return new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }).format(at.toDate())
}

/** The kitchen's quick estimates for an order "čim prej" (§13.5), in minutes from now. */
export const ETA_CHOICES = [15, 30, 45, 60, 90]

/** The quick estimate nearest the house's usual preparation time (the earlier one on a tie). */
export const nearestEta = (prepMinutes: number): number => ETA_CHOICES.reduce((best, choice) =>
    Math.abs(choice - prepMinutes) < Math.abs(best - prepMinutes) ? choice : best, ETA_CHOICES[0])

/** A room-service order the guest wants "čim prej" (no time of their own). */
export const isAsapOrder = (request: IRequest): boolean => request.kind === RequestKind.RoomService && !request.desired_at

/** An RFC 3339 instant as "12:45" on this console's clock ('' for none). */
export const formatClock = (instant: string | undefined): string => {
    if (!instant) return ''

    const at = dayjs(instant)

    if (!at.isValid()) return ''

    return new Intl.DateTimeFormat(intlLocale(), { hour: '2-digit', minute: '2-digit' }).format(at.toDate())
}

/** When the guest wants it: the chosen time, or "čim prej". */
export const desiredLabel = (request: IRequest): string => {
    if (request.kind === RequestKind.Booking) return formatLocal(request.slot_at)
    // Housekeeping has no time: its type leads the summary instead.
    if (request.kind === RequestKind.Housekeeping) return ''
    // A request to the reception (§13.7): its time, a taxi without one now, an extra night none.
    if (request.kind === RequestKind.Reception) {
        if (request.slot_at) return formatLocal(request.slot_at)

        return request.reception?.type === ReceptionType.Taxi ? i18n.global.t('requests.reception_now') : ''
    }

    return request.desired_at ? formatLocal(request.desired_at) : i18n.global.t('requests.asap')
}

/** The house's delivery charge on an order (§13.1.2) — drawn apart from what the guest ordered. */
export const isDeliveryLine = (line: IRequestLine): boolean => line.kind === RequestLineKind.Delivery

/** What the request is, in one line: the invitation, what is missing or broken, or the order's lines (the delivery charge left out). */
export const requestSummary = (request: IRequest): string => {
    if (request.kind === RequestKind.Booking) return request.invitation_title
    if (request.kind === RequestKind.Reception) return receptionSummary(request)
    if (request.kind === RequestKind.Housekeeping) {
        const item = request.housekeeping?.item ?? ''

        return `${housekeepingTypeLabel(request.housekeeping?.type ?? '')}: ${item || request.note}`
    }

    return request.lines.filter((line) => !isDeliveryLine(line)).map((line) => `${line.quantity} × ${line.name}`).join(', ')
}

/** A request to the reception in one line: "Taksi: zdaj · 2 osebi · Letališče" (§13.7). */
export const receptionSummary = (request: IRequest): string => {
    const t = i18n.global.t
    const reception = request.reception
    const parts = [
        desiredLabel(request),
        reception?.nights ? t('requests.reception_nights', { n: reception.nights }) : '',
        request.persons ? t('requests.reception_persons', { n: request.persons }) : '',
        reception?.destination ?? '',
    ].filter(Boolean)

    return `${receptionTypeLabel(reception?.type ?? '')}: ${parts.join(' · ')}`
}

/** The settings of a tenant that never saved them (the API's defaults). */
export const defaultRequestSettings = (): IRequestSettings => ({
    room_service: {
        enabled:             false,
        hours:               [],
        note:                {},
        lead_minutes:        30,
        prep_minutes:        30,
        late_minutes:        20,
        late_cancel_minutes: 10,
        delivery:            { mode: DeliveryMode.Off, item_id: '', name: {}, amount: 0, free_from: 0 },
    },
    max_open:     5,
    max_quantity: 20,
})

/** An invitation's booking that was never set (the API's defaults): off. */
export const defaultBooking = (): IInvitationBooking => ({
    enabled:        false,
    mode:           BookingMode.Slots,
    slots:          [],
    free:           { from: '18:00', to: '21:00', step: 30 },
    capacity:       0,
    max_persons:    4,
    cutoff_minutes: 60,
})
